import { request } from '@/api/client'

export interface AppTemplate {
  id: string
  name: string
  name_zh?: string
  name_en?: string
  thumbnail_url: string
  category?: string
  icon?: string
  default_prompt?: string
  recommend_style_id?: string
  recommend_ratio?: string
  example_images?: string[]
  applicable_style_ids?: string[]
}

export interface AppStyle {
  id: string
  name: string
  thumbnail_url: string
  is_vip: boolean
  sort?: number
  promptSuffix?: string
}

export interface AppRatio {
  id: string
  label: string
  width: number
  height: number
}

export interface TryonReferenceModel {
  id: string
  name: string
  gender: string
  image: string
}

export interface AgreementSection {
  title: string
  title_zh?: string
  title_en?: string
  content: string
  content_zh?: string
  content_en?: string
}

export interface Agreement {
  title: string
  title_zh?: string
  title_en?: string
  sections: AgreementSection[]
}

export interface MembershipBenefit {
  icon: string
  title: string
  title_zh?: string
  title_en?: string
  desc: string
  desc_zh?: string
  desc_en?: string
  limit?: { total: number; unit: string; unit_zh?: string; unit_en?: string } | null
}

export interface PkgBenefit {
  icon: string
  title: string
  title_zh?: string
  title_en?: string
  desc: string
  desc_zh?: string
  desc_en?: string
}

export interface MembershipPackage {
  id: string
  name: string
  name_zh?: string
  name_en?: string
  price: number
  original_price: number
  currency_symbol?: string
  tag?: string
  tag_zh?: string
  tag_en?: string
  is_recommended?: boolean
  points_gift: number
  valid_days: number | null
  accent_color: string
  gradient: string
  benefits: PkgBenefit[]
  apple_product_id?: string
}

export interface PointsRules {
  register_gift: number
  daily_login: number
  generate_cost: number
  prompt_expand_cost?: number
  tool_costs?: Record<string, number>
  mode_costs?: Record<string, number>
}

export interface Contact {
  email: string
  phone?: string | null
  support_hours: string
}

export interface ModelConfig {
  id: string
  name: string
  name_zh?: string
  name_en?: string
  icon?: string
  description?: string
  description_zh?: string
  description_en?: string
  is_new?: boolean
  is_recommended?: boolean
  enabled?: boolean
  quality_points_map?: Record<string, number>
  capabilities?: string[]
  experimental_capabilities?: string[]
  supported_qualities?: string[]
  supported_ratios?: string[]
}

export interface GenerationEntryConfig {
  id: string
  model_ids?: string[]
  required_capabilities: string[]
  prompt_mode: 'freeform' | 'structured' | 'none'
  ai_help_profile?: string | null
  ratio_mode: 'selectable' | 'source' | 'fixed'
  fixed_ratio?: string | null
  quality_mode: 'selectable'
}

export interface TemplateCategory {
  id: string
  name: string
  name_zh?: string
  name_en?: string
  icon?: string
}

export interface ActivityTemplate {
  id: string
  name: string
  name_zh?: string
  name_en?: string
  thumbnail_url: string
  badge?: string
  badge_zh?: string
  badge_en?: string
  default_prompt?: string
  recommend_style_id?: string
  recommend_ratio?: string
}

export interface AppConfig {
  templates: AppTemplate[]
  styles: AppStyle[]
  ratios: AppRatio[]
  pointsPerGeneration?: number
  freePromptExpandCount?: number
  contactUrl?: string
  showcase_images?: Array<{ id: string; url: string; style_label: string; style_key?: string; size?: string }>
  agreements?: Record<string, Agreement>
  points_rules?: PointsRules
  contact?: Contact
  models?: ModelConfig[]
  generation_entries?: GenerationEntryConfig[]
  models_contract_version?: 2
  pricing_config_version?: number
  pricing_config_sha256?: string
  generation_entry_config_bundle_id?: string
  template_categories?: TemplateCategory[]
  activity_templates?: ActivityTemplate[]
  tryon_models?: TryonReferenceModel[]
  ultra_vip_only?: boolean
  default_model?: string
  version?: number
  force_update?: boolean
  hd_download_requires_vip?: boolean
  free_prompt_expand_count?: number
  contact_url?: string
}

export interface ConfigVersion {
  version: number
  force_update: boolean
}

export const getConfigVersion = () =>
  request<ConfigVersion>('GET', '/app/config/version', {
    skipAuth: true,
  })

export const getConfig = () =>
  request<AppConfig>('GET', '/app/config/', {
    skipAuth: true,
  })

// 返回积分矩阵中的最低单次消耗，用于"最多可生成张数"计算
export function getMinPointsCost(config: AppConfig | null | undefined): number | null {
  const modelQualityCosts = (config?.models ?? []).flatMap((model) => (
    (model.supported_qualities ?? []).map((quality) => model.quality_points_map?.[quality])
  )).filter((cost): cost is number => typeof cost === 'number' && cost > 0)

  if (modelQualityCosts.length) {
    return Math.min(...modelQualityCosts)
  }

  return null
}

export function getModelQualityPointsCost(
  config: AppConfig | null | undefined,
  modelId: string,
  quality: string,
): number | null {
  const modelCost = config?.models
    ?.find((model) => model.id === modelId)
    ?.quality_points_map?.[quality]
  if (typeof modelCost === 'number' && modelCost > 0) return modelCost

  return null
}

export const getModelPointsCost = getModelQualityPointsCost

export function modelSupportsGenerationMode(model: ModelConfig, mode: string): boolean {
  const capabilities = new Set(model.capabilities ?? [])
  const experimental = new Set(model.experimental_capabilities ?? [])

  if (mode === 'text2img') return capabilities.has('text_to_image')
  if (mode === 'tryon') {
    return (
      capabilities.has('image_edit') &&
      capabilities.has('multi_image_edit') &&
      (capabilities.has('prompt_based_tryon') || experimental.has('prompt_based_tryon'))
    )
  }
  if (mode === 'img2img' || mode === 'edit' || mode === 'restore') {
    return capabilities.has('image_edit')
  }
  return true
}

export function modelSupportsGenerationEntry(
  model: ModelConfig,
  entry: GenerationEntryConfig | undefined,
  referenceImageCount = 0,
): boolean {
  if (!entry) return false
  if (entry.model_ids && !entry.model_ids.includes(model.id)) return false
  const capabilities = new Set(model.capabilities ?? [])
  const experimental = new Set(model.experimental_capabilities ?? [])
  // prompt_based_tryon is the only explicitly supported experimental runtime
  // capability. Other entry requirements must be promoted to the canonical
  // capability list after Provider evidence exists; the client must not turn
  // arbitrary experimental flags into selectable models.
  if (experimental.has('prompt_based_tryon')) capabilities.add('prompt_based_tryon')
  if (!entry.model_ids && !entry.required_capabilities.every((capability) => capabilities.has(capability))) {
    return false
  }
  if (referenceImageCount > 1 && !capabilities.has('multi_image_edit')) return false

  const qualities = model.supported_qualities ?? []
  const hasPricedQuality = qualities.some((quality) => {
    const points = model.quality_points_map?.[quality]
    return typeof points === 'number' && points > 0
  })
  if (!hasPricedQuality) return false

  const ratios = model.supported_ratios ?? []
  if (entry.ratio_mode === 'fixed') {
    return !!entry.fixed_ratio && ratios.includes(entry.fixed_ratio)
  }
  return ratios.length > 0
}

export function modelSelectionMatchesGenerationEntry(
  model: ModelConfig | undefined,
  entry: GenerationEntryConfig | undefined,
  quality: string,
  ratio: string | null | undefined,
  referenceImageCount = 0,
): boolean {
  if (!model || !modelSupportsGenerationEntry(model, entry, referenceImageCount)) return false
  if (!(model.supported_qualities ?? []).includes(quality)) return false
  const points = model.quality_points_map?.[quality]
  if (typeof points !== 'number' || points <= 0) return false

  const supportedRatios = model.supported_ratios ?? []
  if (entry?.ratio_mode === 'fixed') {
    return !!entry.fixed_ratio && ratio === entry.fixed_ratio && supportedRatios.includes(ratio)
  }
  return !!ratio && supportedRatios.includes(ratio)
}

export function getGenerationEntryConfig(
  config: AppConfig | null | undefined,
  entryId: string,
): GenerationEntryConfig | undefined {
  return config?.generation_entries?.find((entry) => entry.id === entryId)
}

export function getGenerationPointsCost(
  config: AppConfig | null | undefined,
  modelId: string,
  quality: string,
): number | null {
  // Tool and mode identify execution only. Mirror the backend contract so every
  // generation and edit entry presents the selected model/quality price.
  return getModelQualityPointsCost(config, modelId, quality)
}

export interface GenerationPricingQuote {
  expected_cost_points: number
  pricing_config_version: number
  pricing_config_sha256: string
}

export function getGenerationPricingQuote(
  config: AppConfig | null | undefined,
  modelId: string,
  quality: string,
): GenerationPricingQuote | null {
  const expectedCostPoints = getGenerationPointsCost(config, modelId, quality)
  const pricingConfigVersion = config?.pricing_config_version
  const pricingConfigSha256 = config?.pricing_config_sha256
  if (
    expectedCostPoints === null
    || !Number.isInteger(pricingConfigVersion)
    || Number(pricingConfigVersion) < 1
    || typeof pricingConfigSha256 !== 'string'
    || !/^[0-9a-f]{64}$/.test(pricingConfigSha256)
  ) return null
  return {
    expected_cost_points: expectedCostPoints,
    pricing_config_version: Number(pricingConfigVersion),
    pricing_config_sha256: pricingConfigSha256,
  }
}

// ── 分域端点接口 ──────────────────────────────────────────────────────────────

export interface MetaConfig {
  version: number
  force_update: boolean
  hd_download_requires_vip: boolean
  free_prompt_expand_count: number
  ultra_vip_only: boolean
  default_model: string
  contact_url: string
}

export interface TemplatesConfig {
  templates: AppTemplate[]
  template_categories: TemplateCategory[]
  activity_templates: ActivityTemplate[]
}

export interface StylesConfig {
  styles: AppStyle[]
}

export interface DisplayConfig {
  ratios: AppRatio[]
  showcase_images: Array<{ id: string; url: string; style_label: string; style_key?: string; size?: string }>
  tryon_models: TryonReferenceModel[]
}

export interface ModelsConfig {
  contract_version: 2
  models: ModelConfig[]
  entries: GenerationEntryConfig[]
  pricing_config_version: number
  pricing_config_sha256: string
  generation_entry_config_bundle_id: string
}

export class ModelsConfigContractError extends Error {
  readonly code = 'CONFIG_CONTRACT_INCOMPATIBLE'

  constructor(message: string) {
    super(message)
    this.name = 'ModelsConfigContractError'
  }
}

export function assertModelsConfigContract(value: unknown): asserts value is ModelsConfig {
  if (!value || typeof value !== 'object') {
    throw new ModelsConfigContractError('model config payload is missing')
  }
  const config = value as Partial<ModelsConfig>
  if (config.contract_version !== 2) {
    throw new ModelsConfigContractError('model config contract version is not supported')
  }
  if (!Array.isArray(config.models) || config.models.length === 0) {
    throw new ModelsConfigContractError('model config has no public models')
  }
  if (!Array.isArray(config.entries) || config.entries.length === 0) {
    throw new ModelsConfigContractError('model config has no generation entries')
  }
  if (!Number.isInteger(config.pricing_config_version) || Number(config.pricing_config_version) < 1) {
    throw new ModelsConfigContractError('model pricing version is invalid')
  }
  if (typeof config.pricing_config_sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(config.pricing_config_sha256)) {
    throw new ModelsConfigContractError('model pricing digest is invalid')
  }
  if (
    typeof config.generation_entry_config_bundle_id !== 'string'
    || !/^[0-9a-f]{64}$/.test(config.generation_entry_config_bundle_id)
  ) {
    throw new ModelsConfigContractError('generation entry bundle id is invalid')
  }
}

export interface PointsConfig {
  register_gift: number
  daily_login: number
  generate_cost: number
  prompt_expand_cost: number
  tool_costs?: Record<string, number>
  mode_costs?: Record<string, number>
}

// ── 分域 fetch 函数 ───────────────────────────────────────────────────────────

export const getMeta = () =>
  request<MetaConfig>('GET', '/app/config/meta', { skipAuth: true })

export const getTemplatesConfig = () =>
  request<TemplatesConfig>('GET', '/app/config/templates', { skipAuth: true })

export const getStylesConfig = () =>
  request<StylesConfig>('GET', '/app/config/styles', { skipAuth: true })

export const getDisplayConfig = () =>
  request<DisplayConfig>('GET', '/app/config/display', { skipAuth: true })

export const getModelsConfig = () =>
  request<ModelsConfig>('GET', '/app/config/models', { skipAuth: true })

export const getPointsConfig = () =>
  request<PointsConfig>('GET', '/app/config/points', { skipAuth: true })

export const getAgreementsConfig = () =>
  request<Record<string, Agreement>>('GET', '/app/config/agreements', { skipAuth: true })

export const getContactConfig = () =>
  request<Contact>('GET', '/app/config/contact', { skipAuth: true })

// ── 批量端点 ─────────────────────────────────────────────────────────────────

export type BatchConfigResponse = Partial<{
  glopic_meta: MetaConfig
  glopic_templates: TemplatesConfig
  glopic_styles: StylesConfig
  glopic_display: DisplayConfig
  glopic_models: ModelsConfig
  glopic_points: PointsConfig
  glopic_agreements: Record<string, Agreement>
  glopic_contact: Contact
}>

export const getConfigBatch = (keys: string[]) =>
  request<BatchConfigResponse>('GET', `/app/config/batch?keys=${keys.join(',')}`, {
    skipAuth: true,
  })

// 各页面场景所需 key 集合
export const CONFIG_KEYS = {
  HOME: ['glopic_meta', 'glopic_display', 'glopic_templates', 'glopic_models', 'glopic_points'],
  GENERATE: ['glopic_templates', 'glopic_styles', 'glopic_display', 'glopic_models', 'glopic_points'],
  LOGIN: ['glopic_agreements', 'glopic_points'],
  SETTINGS: ['glopic_contact', 'glopic_agreements'],
  ALL: [
    'glopic_meta', 'glopic_templates', 'glopic_styles', 'glopic_display',
    'glopic_models', 'glopic_points', 'glopic_agreements', 'glopic_contact',
  ],
} as const
